import React, { useEffect, useRef } from 'react';
import { useModel } from '@umijs/max';
import { usePermissionStore } from '@/stores/permissionStore';
import { permissionWebSocket } from '@/services/websocket/permissionWebSocket';
import { useShallow } from 'zustand/react/shallow';

/**
 * Synchronizes the permission store into Umi initial state. Startup rendering
 * is owned by LayoutPermissionGuard so the app has only one loading gate.
 */
const PermissionAwareMenu: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { initialState, setInitialState } = useModel('@@initialState');
  const {
    userPermissions,
    isInitialized,
    isLoadingGroups,
    isLoadingUserPermissions,
    lastUserPermissionsFetch,
    userPermissionsError,
  } = usePermissionStore(
    useShallow((state) => ({
      userPermissions: state.userPermissions,
      isInitialized: state.isInitialized,
      isLoadingGroups: state.isLoadingGroups,
      isLoadingUserPermissions: state.isLoadingUserPermissions,
      lastUserPermissionsFetch: state.lastUserPermissionsFetch,
      userPermissionsError: state.userPermissionsError,
    })),
  );
  const lastSyncedSignatureRef = useRef<string | null>(null);
  const userId =
    initialState?.currentUser?.id?.toString?.() || initialState?.currentUser?.userid || '';

  useEffect(() => {
    if (!userId) return;
    permissionWebSocket.connect();
    return () => permissionWebSocket.disconnect();
  }, [userId]);

  useEffect(() => {
    if (!userId || userPermissionsError) {
      return;
    }

    if (isLoadingGroups || isLoadingUserPermissions) {
      return;
    }

    if (!isInitialized && lastUserPermissionsFetch === null) {
      return;
    }

    const permissionCodes = userPermissions
      .map((permission) => permission?.code)
      .filter((code): code is string => Boolean(code));

    const sortedCodes = [...new Set(permissionCodes)].sort();
    const signature = `${userId}|${sortedCodes.join(',')}`;

    if (lastSyncedSignatureRef.current === signature) {
      return;
    }

    lastSyncedSignatureRef.current = signature;

    setInitialState((prevState: any) => {
      if (!prevState?.currentUser) {
        return prevState;
      }

      const previousCodes = Array.isArray(prevState.permissions)
        ? [...prevState.permissions].sort()
        : [];

      const hasSameCodes =
        previousCodes.length === sortedCodes.length &&
        previousCodes.every((code, index) => code === sortedCodes[index]);

      if (hasSameCodes && prevState.permissionsLoaded) {
        return prevState;
      }

      return {
        ...prevState,
        permissions: sortedCodes,
        permissionsLoaded: true,
      };
    });
  }, [
    isInitialized,
    isLoadingGroups,
    isLoadingUserPermissions,
    lastUserPermissionsFetch,
    setInitialState,
    userId,
    userPermissions,
    userPermissionsError,
  ]);

  return <>{children}</>;
};

export default PermissionAwareMenu;
